import "isomorphic-unfetch";
import { createSlice, createAsyncThunk, isAnyOf } from "@reduxjs/toolkit";
import buildGeoHeaders from "utils/geoHeaders";
import { v4 as uuidv4 } from "uuid";
import { forwardLog } from "utils/logForwarder";
import { getFacilityPage } from "./facilities";

export const initialState = {
	baseUrl: "",
	apiHost: "",
	trackingUrl: "",
	trackingId: "",
	id: null,
	segmentWriteKey: null,
	optimizelySdkKey: "",
	statusCode: 200,
	experiments: {},
	status: null,
	error: null,
	ready: false,
	geolocatedFacilities: null,
	nearbyCities: null
};

export function createPageAction({ actionName, uri }) {
	return createAsyncThunk(actionName, async (_id, thunkAPI) => {
		const state = thunkAPI.getState();
		const {
			app: { apiHost, apiToken, id, trackingId },
			user: { visitId, geolocation }
		} = state;
		const fullUrl = `${apiHost}${uri}`;
		const headers = {
			token: apiToken,
			"app-id": id,
			"visit-id": visitId,
			"X-Trace-Id": trackingId,
			...buildGeoHeaders(geolocation)
		};

		const initMsg = `[X-Trace-Id: ${trackingId}] → GET ${fullUrl} | Action: ${actionName} | Request initiated`;
		// eslint-disable-next-line no-console
		console.log(initMsg);
		forwardLog({ level: "info", message: initMsg, "x-trace-id": trackingId });
		const startTime = Date.now();

		try {
			const response = await fetch(fullUrl, { headers });
			const duration = Date.now() - startTime;
			const successMsg = `[X-Trace-Id: ${trackingId}] ← GET ${fullUrl} | Action: ${actionName} | Status: ${response.status} | ${duration}ms`;
			// eslint-disable-next-line no-console
			console.log(successMsg);
			forwardLog({ level: "info", message: successMsg, "x-trace-id": trackingId });
			return response.json();
		} catch (error) {
			const duration = Date.now() - startTime;
			const errorMsg = `[X-Trace-Id: ${trackingId}] ✕ GET ${fullUrl} | Action: ${actionName} | Error: ${error?.message || "Unknown"} | ${duration}ms`;
			// eslint-disable-next-line no-console
			console.error(errorMsg);
			forwardLog({ level: "error", message: errorMsg, "x-trace-id": trackingId });
			throw error;
		}
	});
}

export const fetchDefaultPage = createPageAction({
	actionName: "app/fetchDefaultPage",
	uri: "/api/page/default"
});

export const appSlice = createSlice({
	name: "app",
	initialState,
	reducers: {
		setAppReady: (state) => {
			state.ready = true;
		},
		renderError: (state, action) => {
			state.statusCode = 500;
			state.loading = false;
			state.error = action.payload;
		},
		renderNotFound: (state) => {
			state.statusCode = 404;
			state.loading = false;
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchDefaultPage.pending, (state) => {
				state.status = "loading";
			})
			.addCase(fetchDefaultPage.fulfilled, (state, action) => {
				state.status = "loaded";
				state.experiments = action.payload.experiments;
				const geolocatedFacilities =
					action.payload?.geolocatedFacilities;
				if (geolocatedFacilities) {
					state.geolocatedFacilities = geolocatedFacilities;
					state.nearbyCities =
						geolocatedFacilities.nearbyCities || null;
				}
			})
			.addMatcher(isAnyOf(getFacilityPage.rejected), (state, action) => {
				state.statusCode = 404;
				state.loading = false;
				state.error = action?.error?.message || "Error loading page";
			});
	}
});

export const { renderError, renderNotFound, setAppReady } = appSlice.actions;

export default appSlice.reducer;
